import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import "./ContactFormModal.css";

const overlayInlineStyle = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backgroundColor: "transparent",
  zIndex: 9998,
};

const contentInlineStyle = {
  position: "relative",
  inset: "auto",
  padding: 0,
  margin: 0,
  background: "transparent",
  border: "none",
  overflow: "visible",
  borderRadius: 0,
  width: "100%",
  maxWidth: "540px",
  maxHeight: "calc(100vh - 40px)",
};

const initialForm = { name: "", email: "", message: "" };

const ContactFormModal = ({
  isOpen,
  onClose,
  title,               // "Booking" | "Contact"
  artistName,          // e.g. "Concrete"
  artistRole = "Comedian", // "Comedian" | "Content Creator"
  formAction,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const isBooking = title === "Booking";
  const isSubmitting = status.state === "submitting";
  const inquiryLabel = isBooking ? "Booking Request" : "Contact Inquiry";

  // Reset when the modal closes so the next open is clean
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialForm);
      setStatus({ state: "idle", message: "" });
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "Sending your message…" });

    try {
      const response = await fetch(formAction, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: "your-web3forms-access-key",
          subject: `${title} request: ${artistName}`,
          inquiry_type: title,
          artist: artistName,
          role: artistRole,
          ...formData,
        }),
      });

      if (response.ok) {
        setStatus({
          state: "success",
          message: `Message sent. We'll be in touch about ${artistName} shortly.`,
        });
        setFormData(initialForm);
      } else {
        setStatus({
          state: "error",
          message: "Failed to send message. Please try again later.",
        });
      }
    } catch (err) {
      setStatus({
        state: "error",
        message: "Network error. Please try again later.",
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      closeTimeoutMS={220}
      style={{ overlay: overlayInlineStyle, content: contentInlineStyle }}
      className={{
        base: "artist-modal",
        afterOpen: "artist-modal--after-open",
        beforeClose: "artist-modal--before-close",
      }}
      overlayClassName={{
        base: "artist-modal-overlay",
        afterOpen: "artist-modal-overlay--after-open",
        beforeClose: "artist-modal-overlay--before-close",
      }}
    >
      <button
        type="button"
        className="artist-modal-close"
        onClick={onClose}
        aria-label="Close"
        disabled={isSubmitting}
      >
        <span aria-hidden="true">×</span>
      </button>

      <header className="artist-modal-header">
        <span className="artist-modal-eyebrow">
          {inquiryLabel}
        </span>
        <h2 className="artist-modal-title">{artistName}</h2>
        <p className="artist-modal-subtitle">{artistRole}</p>
      </header>

      <form onSubmit={handleSubmit} className="artist-modal-form" noValidate>
        <div className="artist-modal-row">
          <div className="artist-modal-field">
            <label htmlFor="am-name" className="artist-modal-label">Your Name</label>
            <input
              id="am-name"
              type="text"
              name="name"
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
              autoComplete="name"
              required
            />
          </div>
          <div className="artist-modal-field">
            <label htmlFor="am-email" className="artist-modal-label">Email</label>
            <input
              id="am-email"
              type="email"
              name="email"
              placeholder="you@email.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>
        </div>

        <div className="artist-modal-field">
          <label htmlFor="am-message" className="artist-modal-label">Message</label>
          <textarea
            id="am-message"
            name="message"
            placeholder={
              isBooking
                ? `Share details about the event you'd like to book ${artistName} for.`
                : `Let us know what you'd like to discuss regarding ${artistName}.`
            }
            rows="5"
            value={formData.message}
            onChange={handleChange}
            required
          />
        </div>

        <button
          type="submit"
          className="artist-modal-submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          <span className="artist-modal-submit-label">
            {isSubmitting
              ? "Sending"
              : isBooking
              ? "Submit Booking"
              : "Send Message"}
          </span>
          <span className="artist-modal-submit-arrow" aria-hidden="true">
            {isSubmitting ? "…" : "→"}
          </span>
        </button>

        {status.state !== "idle" && (
          <p
            className={`artist-modal-status artist-modal-status--${status.state}`}
            role="status"
            aria-live="polite"
          >
            {status.message}
          </p>
        )}
      </form>
    </Modal>
  );
};

export default ContactFormModal;
